import { Box, Flex } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { SubmitHandler, useFormContext, useWatch } from 'react-hook-form';

import classNames from 'classnames/bind';
import { ButtonIcon, Show } from 'shared';
import { Icon, iconNames } from 'shared_DEPRECATED/components/Icon';
import Spacer from 'shared_DEPRECATED/components/Spacer/Spacer';

import ChatAttachment from 'features/chat/components/ChatAttachment/ChatAttachment';
import { ChatFormMessageEdit } from 'features/chat/components/ChatForm/components/MessageEdit';
import { ChatFormMessageReply } from 'features/chat/components/ChatForm/components/MessageReply';
import { MicrophonePermissionsRequiredErrorMessage } from 'features/chat/components/ChatForm/components/MicrophonePermissionsRequiredErrorMessage';
import { ChatMessageLinkPreviewSmall } from 'features/chat/components/ChatMessages/ChatMessageLinkPreviewSmall';
import { CHAT_FORM_FIELD_NAMES } from 'features/chat/config';
import { ChatFormValues } from 'features/chat/config/types/ChatForm';
import { useChatInterlocutorContext } from 'features/chat/context/chatInterlocutor';
import { useChatMessagesContext } from 'features/chat/context/chatMessages';
import { useChatFormOrchestrator } from 'features/chat/hooks/useChatFormOrchestrator';
import { useChatFormSubmit } from 'features/chat/hooks/useChatFormSubmit';
import { useFocusOnParentChange } from 'features/chat/hooks/useFocusOnParentChange';
import { useGetFirstLinkPreview } from 'features/chat/hooks/useGetFirstLinkPreview';
import { useIsMorpheus3WayChatFFEnabled } from 'features/chat/hooks/useIsMorpheus3WayChatFFEnabled';
import { usePersistChatMessageText } from 'features/chat/hooks/usePersistChatMessageText';
import { RenderIfCoach } from 'features/user/components/RenderIfCoach';

import { ScheduleButtonContainer } from '../ChatMessagesScheduling/ScheduleButtonContainer';
import { ChatFormRecordingMode } from './components/RecordingMode';
import { ChatFormTextMode } from './components/TextMode';
import styles from 'features/chat/components/Chat.module.css';

const cx = classNames.bind(styles);

export type ChatFormProps = {
  disabled: boolean;
};

export function ChatForm({ disabled }: ChatFormProps) {
  const methods = useFormContext<ChatFormValues>();

  const messageText = useWatch({
    control: methods.control,
    name: CHAT_FORM_FIELD_NAMES.TEXT,
  });

  const isMorpheus3WayChatFFEnabled = useIsMorpheus3WayChatFFEnabled();

  const { name } = useChatInterlocutorContext();
  useFocusOnParentChange();
  usePersistChatMessageText();

  const {
    isTextMode,
    isRecordingPaused,
    initiateRecording,
    micPermissionsDenied,
    cancelRecording,
    stopRecording,
    pauseRecording,
    getRecordedAttachment,
  } = useChatFormOrchestrator({
    hasText: !!messageText,
  });

  const {
    onFormSubmit,
    onScheduledAudioRecordingSubmit,
    onScheduledTextSubmit,
  } = useChatFormSubmit({
    isTextMode,
    pauseRecording,
    stopRecording,
    cancelRecording,
    getRecordedAttachment,
  });

  const {
    parentMessage,
    setParentMessage,
    draftMessageId,
    setDraftMessageId,
    editedMessage,
    setEditedMessage,
    sendMorpheusMessage,
  } = useChatMessagesContext();

  const linkPreview = useGetFirstLinkPreview({ text: messageText });

  useEffect(() => {
    if (messageText.length === 0 && draftMessageId) {
      setDraftMessageId(null);
    }
  }, [messageText, setDraftMessageId, draftMessageId]);

  const onRecordingStop = () => {
    handleFormSubmit({ text: '' });
  };

  const handleFormSubmit: SubmitHandler<ChatFormValues> = async (values) => {
    const { text } = values;

    onFormSubmit(text, linkPreview);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!disabled && !event.shiftKey && event.key === 'Enter') {
      event.preventDefault();
      methods.handleSubmit(handleFormSubmit)();
    }
  };

  return (
    <Box bg="var(--bgSecondary)">
      {parentMessage ? (
        <Box pl="14" pr="6">
          <ChatFormMessageReply
            messageId={parentMessage.messageId}
            attachments={parentMessage.attachments}
            text={parentMessage.text}
            name={name}
            onCancel={() => setParentMessage(null)}
          />
        </Box>
      ) : null}
      {editedMessage ? (
        <Box pl="14" pr="6">
          <ChatFormMessageEdit onCancel={() => setEditedMessage(null)} />
        </Box>
      ) : null}
      <Show when={!!linkPreview}>
        {() => (
          <Box pl="14" pr="6">
            <ChatMessageLinkPreviewSmall
              title={linkPreview!.title}
              siteName={linkPreview!.siteName}
              url={linkPreview!.url}
            />
          </Box>
        )}
      </Show>
      <form
        className={cx('chat__form')}
        onSubmit={methods.handleSubmit(handleFormSubmit)}
        data-testid="chat-form"
      >
        <Spacer size="md">
          {micPermissionsDenied && (
            <MicrophonePermissionsRequiredErrorMessage />
          )}
          <Spacer size="sm sm md">
            <Flex alignItems="center" gap="var(--spacing02)">
              <ChatAttachment disabled={disabled} />
              <Show when={isMorpheus3WayChatFFEnabled}>
                <ButtonIcon
                  bg="var(--bgPrimary)"
                  size="md"
                  onClick={() => sendMorpheusMessage()}
                >
                  <Icon
                    name={iconNames.sparkles}
                    height="20px"
                    width="20px"
                    fill="var(--fgSecondary)"
                  />
                </ButtonIcon>
              </Show>
              {isTextMode ? (
                <ChatFormTextMode
                  disabled={disabled}
                  handleKeyDown={handleKeyDown}
                  startRecording={initiateRecording}
                  isEditing={!!editedMessage}
                >
                  <RenderIfCoach>
                    <ScheduleButtonContainer
                      text={messageText}
                      linkPreview={linkPreview}
                      onSubmit={onScheduledTextSubmit}
                    />
                  </RenderIfCoach>
                </ChatFormTextMode>
              ) : (
                <ChatFormRecordingMode
                  onSubmit={onScheduledAudioRecordingSubmit}
                  disabled={disabled}
                  isRecordingPaused={isRecordingPaused}
                  cancelRecording={cancelRecording}
                  onRecordingStop={onRecordingStop}
                />
              )}
            </Flex>
          </Spacer>
        </Spacer>
      </form>
    </Box>
  );
}
